import mixins from 'vue-typed-mixins';

import { E入力分類, E補助科目フラグ, Eフェーズ種別 } from '@/openapi/enum';
import { データApi, データListItemRequest, 勘定科目Api, 部門Api } from '@/openapi/user';

import { KeystrokeInputableTextId } from '@/components/keystrokeInputableText/KeystrokeInputableText';
import MoneyUnit決算期TableHeader from '@/components/moneyUnit決算期TableHeader/MoneyUnit決算期TableHeader.vue';
import C決算期TableHeader from '@/components/決算期TableHeader/決算期TableHeader.vue';
import C返済原資Cell from '@/entries/user/pages/authorized/事業所/計画書/計画/借入返済/返済計画/返済原資Cell/返済原資Cell.vue';
import C金融機関Cell from '@/entries/user/pages/authorized/事業所/計画書/計画/借入返済/返済計画/金融機関Cell/金融機関Cell.vue';
import C保存Button from '@/components/保存Button/保存Button.vue';
import C計画書データ表示設定CheckBox from '@/components/計画書データ表示設定CheckBox/計画書データ表示設定CheckBox.vue';

import KeystrokeInputableTextMixin from '@/entries/user/mixins/KeystrokeInputableTextMixin';
import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';

import 返済計画Service, {
  CellTagType,
  YMValue,
  返済原資Type,
  返済計画,
  金融機関ListItem,
  金融機関返済Type,
} from '@/classes/service/計画/返済計画Service';
import { 金融機関借入ListItem } from '@/classes/service/分析計画/金融機関借入Service';
import UserApiUtility from '@/classes/common/UserApiUtility';

export default mixins(KeystrokeInputableTextMixin, RoutesMixin, 計画書Mixin, 事業所Mixin).extend({
  name: 'C返済計画',

  components: {
    MoneyUnit決算期TableHeader,
    C決算期TableHeader,
    C返済原資Cell,
    C金融機関Cell,
    C保存Button,
    C計画書データ表示設定CheckBox,
  },

  data(): {
    readonly cellTagType: typeof CellTagType;
    api: {
      b: 部門Api;
      d: データApi;
      k: 勘定科目Api;
    };
    v返済計画: 返済計画 | null;
    service: 返済計画Service;
    isEditing: boolean;
  } {
    return {
      cellTagType: CellTagType,
      api: {
        b: UserApiUtility.ogApi(部門Api),
        d: UserApiUtility.ogApi(データApi),
        k: UserApiUtility.ogApi(勘定科目Api),
      },
      v返済計画: null,
      service: new 返済計画Service(Eフェーズ種別.計画フェーズ),
      isEditing: false,
    };
  },

  filters: {
    返済原資RowCss(type: 返済原資Type): string {
      switch (type) {
        case 返済原資Type.FCF:
        case 返済原資Type.原資合計:
          return 'subHeaderRow';
        default:
          return '';
      }
    },
    金融機関RowCss(type: 金融機関返済Type, isTotal: boolean): string {
      switch (type) {
        case 金融機関返済Type.期首残高:
        case 金融機関返済Type.新規借入:
        case 金融機関返済Type.期末残高:
        case 金融機関返済Type.残高シェア:
          return 'subHeaderRow';
        case 金融機関返済Type.返済額:
          return isTotal ? 'specialHeaderRow' : '';
      }
    },
  },

  computed: {
    map(): (KeystrokeInputableTextId | null)[][] {
      if (this.v返済計画 === null) return [];

      const canInput = (type: CellTagType | undefined): boolean =>
        type !== undefined &&
        type !== CellTagType.currency &&
        type !== CellTagType.textLeft &&
        type !== CellTagType.textRight;

      const 返済原資Map = this.v返済計画.返済原資List.map((item, i) => [
        canInput(item.label.tagType) ? { type: 0, i, j: -1 } : null,
        ...Array(4).fill(null),
        ...item.values.map((value, j) => (canInput(value.tagType) ? { type: 0, i, j } : null)),
      ]);
      const 金融機関Map = this.金融機関List.map((item, i) =>
        item.cells.map((cell, j) => (canInput(cell?.tagType) ? { type: 1, i, j } : null))
      );

      return [...返済原資Map, ...金融機関Map];
    },
    金融機関List(): 金融機関ListItem[] {
      if (this.v返済計画 === null) return [];

      return this.v返済計画.金融機関GroupList.flatMap((group) => group.list);
    },
  },

  async created(): Promise<void> {
    await this.$_load();
    this.setFirstKeystrokeId(this.map);
  },

  methods: {
    change返済原資(colIndex: number): void {
      if (this.v返済計画 === null) return;
      this.service.calculate返済原資(this.v返済計画, colIndex);
      this.isEditing = true;
    },
    change金融機関返済額(ymInput: YMValue, item: 金融機関ListItem): void {
      if (this.v返済計画 === null || item.isTotal || item.type !== 金融機関返済Type.返済額) return;

      // Cデータ金融機関借入に存在しない場合は追加
      const 返済額 = item.借入!.返済額;
      const find = 返済額.find((item) => item.ym === ymInput.ym);
      if (find === undefined) 返済額.push(ymInput);

      const findCalc = this.v返済計画.金融機関CalcList.find((v) => v.金融機関借入?.データpk === item.借入?.データpk);
      if (findCalc !== undefined) {
        const 元本返済 = findCalc.金融機関借入!.元本返済;
        // Find first item in 元本返済 matching condition
        const find元本返済 = 元本返済.find((value) => value.ym === Math.floor(ymInput.ym / 100));
        if (find元本返済 === undefined) {
          // Find first item in list.values matching condition
          const findFixValue = findCalc.list
            .find((li) => li.type === 金融機関返済Type.返済額)
            ?.values.find((val) => val.決算期ym === ymInput.ym);
          if (findFixValue !== undefined) {
            元本返済.push({
              value: ymInput.value - (ymInput.oldValue ?? 0) + (findFixValue.fix as number),
              ym: Math.floor(ymInput.ym / 100),
            });
          }
        } else find元本返済.value = ymInput.value - (ymInput.oldValue ?? 0) + find元本返済.value;
      }

      // 再計算
      this.service.calculate金融機関返済(this.v返済計画);
      this.isEditing = true;
    },
    calc金融機関返済(): void {
      if (this.v返済計画 === null) return;

      // 返済開始時期のバリデーション
      let msg = '';
      const 返済開始時期 = this.v返済計画.cデータ返済計画.返済開始時期;
      if (返済開始時期 === undefined || 返済開始時期 === null) {
        // 未入力（undefined）、消去（null）した状態は不可
        msg = '返済開始時期を入れてください';
      } else if (Number(返済開始時期) <= this.計画書.c実績直近月) {
        // c実績直近月以前の年月は不可
        msg = '返済開始時期は、直近月より後の年月を入れてください';
      }
      if (msg.length > 0) {
        alert(msg);
        return;
      }

      // 再計算
      this.service.calculate金融機関返済(this.v返済計画, true, true);
    },
    async save(): Promise<void> {
      if (this.v返済計画 === null) return;

      // リクエスト生成
      // 返済計画のデータでrequestsを初期化
      const requests: データListItemRequest[] = [
        {
          pk: this.v返済計画.データpk,
          c入力分類: E入力分類.返済計画,
          c番号: 0,
          cフェーズ種別: Eフェーズ種別.計画フェーズ,
          cデータ: JSON.stringify(this.v返済計画.cデータ返済計画),
        },
      ];
      // 金融機関データがあればrequestsに追加
      this.service.calculate元本返済(this.v返済計画);
      const 金融機関借入Requests: データListItemRequest[] = this.v返済計画.金融機関CalcList
        .filter((item) => item.金融機関借入 !== undefined)
        .map((item) => {
          return this.$_create金融機関借入データListItemRequest(item.金融機関借入!);
        });
      if (金融機関借入Requests.length > 0) requests.push(...金融機関借入Requests);

      // API呼び出し
      await this.api.d.bulkSaveデータ(this.計画書.pk, requests);
      await this.$_load();
    },
    // async saveDetail(): Promise<void> {
    //   if (this.v返済計画 === null) return;

    //   this.service.calculate元本返済(this.v返済計画);
    //   const 金融機関借入Requests = this.v返済計画.金融機関CalcList
    //     .filter((item) => item.金融機関借入 !== undefined)
    //     .map((item) => this.$_create金融機関借入データListItemRequest(item.金融機関借入!));

    //   // API呼び出し
    //   if (金融機関借入Requests.length > 0) await this.api.d.bulkSaveデータ(this.計画書.pk, 金融機関借入Requests);
    //   this.isEditing = false;
    // },
    onChangeフリーキャッシュフロー種別(): void {
      if (this.v返済計画 === null) return;

      this.service.update返済原資List(this.v返済計画);
      for (let i = 0; i < this.v返済計画.cデータ返済計画.yearlyValues.length; i++) {
        this.service.calculate返済原資(this.v返済計画, i);
      }
      this.service.calculate金融機関返済(this.v返済計画, true);
    },
    async $_load(): Promise<void> {
      const 部門Request = this.api.b.list部門(this.事業所.pk);
      const 勘定科目Request = this.api.k.list勘定科目(this.事業所.pk, E補助科目フラグ.通常);
      const データRequest = this.api.d.listデータ(this.計画書.pk, this.service.getRelationalE入力分類s(), undefined, {
        params: {
          sj: '返済計画',
        },
      });
      const responses = await Promise.all([部門Request, 勘定科目Request, データRequest]);
      this.service.set部門s = responses[0].data;
      this.service.set勘定科目s = responses[1].data;
      this.v返済計画 = this.service.parse(responses[2].data);

      this.isEditing = false;
    },
    $_create金融機関借入データListItemRequest(item: 金融機関借入ListItem): データListItemRequest {
      const { データpk, 番号, フェーズ種別, ...金融機関借入 } = item;
      return {
        pk: データpk!,
        c入力分類: E入力分類.金融機関借入,
        c番号: 番号!,
        cフェーズ種別: フェーズ種別!,
        cデータ: JSON.stringify(金融機関借入),
      };
    },
  },
});
